export type Role =
  | 'admin'
  | 'kaufmann'
  /** Betriebsprüfer: sieht alles Steuerrelevante, ändert nichts. */
  | 'pruefer'
  | 'disponent'
  | 'schichtleiter'
  | 'lagerist'
  | 'auftraggeber';

export interface User {
  id: string;
  tenantId: string;
  email: string;
  name: string;
  role: Role;
  ownerId: string | null;
}

export interface Partner {
  id: string;
  number: string;
  name: string;
  is_customer: boolean;
  is_supplier: boolean;
  is_manufacturer?: boolean;
  is_owner: boolean;
  is_self: boolean;
  email: string | null;
  phone: string | null;
  street: string | null;
  zip: string | null;
  city: string | null;
  country: string;
  note: string | null;
  active: boolean;
  debitor_account: number | null;
  kreditor_account: number | null;
  vat_id: string | null;
  buyer_reference: string | null;
  tax_treatment: TaxTreatment;
}

export interface Article {
  id: string;
  owner_id: string;
  owner_name: string;
  sku: string;
  name: string;
  description: string | null;
  ean: string | null;
  manufacturer_sku: string | null;
  units_per_carton: number | null;
  unit: string;
  weight_g: number | null;
  length_mm: number | null;
  width_mm: number | null;
  height_mm: number | null;
  hazmat: boolean;
  requires_lot: boolean;
  requires_serial: boolean;
  requires_best_before: boolean;
  min_stock: number;
  purchase_price_cents: number | null;
  sale_price_cents: number | null;
  manufacturer_id: string | null;
  manufacturer_name: string | null;
  vk_brutto_cents: number | null;
  tax_rate_percent: number | null;
  einstand_cents: number | null;
  product_group_id: string | null;
  product_group_name: string | null;
  adr_un_nummer: string | null;
  adr_benennung: string | null;
  adr_klasse: string | null;
  adr_verpackungsgruppe: string | null;
  adr_klassifizierungscode: string | null;
  adr_gefahrzettel: string | null;
  adr_tunnelcode: string | null;
  adr_kategorie: number | null;
  adr_menge_je_einheit: number | null;
  adr_begrenzte_menge: boolean;
  /** Die Zeile fürs Beförderungspapier, sofern die Angaben vollständig sind. */
  adr_angabe: string | null;
  adr_fehlt: string[];
  active: boolean;
  stock_total: number;
  kind: 'artikel' | 'variantenvater' | 'set';
  parent_article_id: string | null;
  parent_name: string | null;
  variant_attributes: string[] | null;
  variant_values: Record<string, string> | null;
  variant_count: number;
  component_count: number;
  buildable: number | null;
}

export interface Component {
  id: string;
  component_article_id: string;
  sku: string;
  article_name: string;
  unit: string;
  qty: number;
  position: number;
  stock_total: number;
}

export interface Warengruppe {
  id: string;
  parent_id: string | null;
  name: string;
  active: boolean;
  sortierung: number;
  ebene: number;
  pfad: string;
  artikel_direkt: number;
  kinder: number;
}

export interface Gruppenauswertung {
  id: string;
  name: string;
  pfad: string;
  ebene: number;
  artikel: number;
  bestand_einheiten: number | null;
  einheit: string | null;
  bestandswert_ek_cents: number;
  spanne_schnitt: number | null;
}

export interface Warehouse {
  id: string;
  code: string;
  name: string;
  street: string | null;
  zip: string | null;
  city: string | null;
  active: boolean;
  location_count: number;
}

export interface Zone {
  id: string;
  warehouse_id: string;
  code: string;
  name: string;
}

export interface Location {
  id: string;
  warehouse_id: string;
  warehouse_code: string;
  zone_id: string | null;
  zone_code: string | null;
  code: string;
  name: string | null;
  type: string;
  is_virtual: boolean;
  capacity_units: number | null;
  exclusive_owner_id: string | null;
  active: boolean;
  used_qty: number;
  article_count: number;
  is_pick_face: boolean;
  fixed_article_id: string | null;
  fixed_article_name: string | null;
  replenish_min: number | null;
  replenish_target: number | null;
}

export interface StockRow {
  owner_id: string;
  owner_name: string;
  article_id: string;
  sku: string;
  article_name: string;
  unit: string;
  location_id: string;
  location_code: string;
  warehouse_code: string;
  lot_id: string | null;
  lot_code: string | null;
  best_before: string | null;
  qty: number;
}

export type MovementReason =
  | 'wareneingang'
  | 'warenausgang'
  | 'umlagerung'
  | 'korrektur'
  | 'inventur'
  | 'retoure';

export interface Movement {
  id: string;
  booked_at: string;
  reason: MovementReason;
  qty: number;
  owner_name: string;
  sku: string;
  article_name: string;
  unit: string;
  from_code: string | null;
  to_code: string | null;
  lot_code: string | null;
  serial: string | null;
  document_ref: string | null;
  note: string | null;
  user_name: string | null;
}

export interface Lot {
  id: string;
  article_id: string;
  code: string;
  best_before: string | null;
  supplier_ref: string | null;
}

export interface Dashboard {
  kpi: {
    articles: number;
    owners: number;
    locationsTotal: number;
    locationsOccupied: number;
    stockUnits: number;
    stockValueCents: number;
    movementsToday: number;
    belowMin: number;
    openCounts: number;
    failedJobs: number;
  };
  perDay: { tag: string; eingang: number; ausgang: number }[];
  topOwners: { owner_id: string; owner_name: string; units: number; articles: number }[];
  recentMovements: {
    id: string;
    booked_at: string;
    reason: string;
    qty: number;
    sku: string;
    article_name: string;
    from_code: string | null;
    to_code: string | null;
    user_name: string | null;
  }[];
  alerts: { sku: string; article_name: string; owner_name: string; stock: number; min_stock: number }[];
}

export interface StockCount {
  id: string;
  warehouse_id: string;
  warehouse_code: string;
  owner_id: string | null;
  owner_name: string | null;
  name: string;
  status: 'offen' | 'gezaehlt' | 'gebucht' | 'verworfen';
  created_at: string;
  closed_at: string | null;
  line_count: number;
  counted_count: number;
  difference_count: number;
}

export interface CountLine {
  id: string;
  owner_id: string;
  owner_name: string;
  article_id: string;
  sku: string;
  article_name: string;
  unit: string;
  location_id: string;
  location_code: string;
  lot_id: string | null;
  lot_code: string | null;
  qty_expected: number;
  qty_counted: number | null;
  counted_at: string | null;
}

export interface ManagedUser {
  id: string;
  email: string;
  username: string | null;
  name: string;
  role: Role;
  owner_id: string | null;
  owner_name: string | null;
  active: boolean;
  last_login_at: string | null;
  created_at: string;
  /** Hat einen aktiven, verknüpften Kassierer-Eintrag. */
  ist_kassierer: boolean;
}

export type InboundStatus = 'angekuendigt' | 'teilweise' | 'abgeschlossen' | 'storniert';

export interface InboundOrder {
  id: string;
  number: string;
  reference: string | null;
  owner_id: string;
  owner_name: string;
  supplier_id: string | null;
  supplier_name: string | null;
  warehouse_id: string;
  warehouse_code: string;
  expected_at: string | null;
  status: InboundStatus;
  note: string | null;
  created_at: string;
  closed_at: string | null;
  line_count: number;
  qty_expected: number;
  qty_received: number;
}

export interface InboundLine {
  id: string;
  position: number;
  article_id: string;
  sku: string;
  article_name: string;
  unit: string;
  requires_lot: boolean;
  requires_serial: boolean;
  requires_best_before: boolean;
  qty_expected: number;
  qty_received: number;
  lot_code: string | null;
  best_before: string | null;
  note: string | null;
}

export interface PutawaySuggestion {
  location_id: string;
  code: string;
  reason: string;
}

export type OutboundStatus =
  | 'offen'
  | 'kommissionierung'
  | 'kommissioniert'
  | 'versendet'
  | 'storniert';

export interface OutboundOrder {
  /** Angebot oder Auftrag - derselbe Beleg in zwei Zuständen. */
  kind: 'angebot' | 'auftrag';
  valid_until: string | null;
  invoiced_at: string | null;
  id: string;
  number: string;
  reference: string | null;
  owner_id: string;
  owner_name: string;
  customer_id: string | null;
  customer_name: string | null;
  warehouse_id: string;
  warehouse_code: string;
  requested_at: string | null;
  priority: number;
  status: OutboundStatus;
  note: string | null;
  fond_id: string | null;
  fond_bezeichnung: string | null;
  fond_kennung: string | null;
  deliver_company: string | null;
  deliver_first_name: string | null;
  deliver_last_name: string | null;
  deliver_street: string | null;
  deliver_zip: string | null;
  deliver_city: string | null;
  deliver_info: string | null;
  created_at: string;
  shipped_at: string | null;
  line_count: number;
  qty_ordered: number;
  qty_picked: number;
  qty_shipped: number;
  open_tasks: number;
  packages: number | null;
  weight_kg: number | null;
  tracking_ref: string | null;
  carrier: string | null;
  packing_started_at: string | null;
}

export interface OutboundLine {
  unit_price_cents: number | null;
  discount_percent: number;
  amount_cents: number;
  id: string;
  position: number;
  article_id: string;
  sku: string;
  article_name: string;
  unit: string;
  qty_ordered: number;
  qty_picked: number;
  qty_shipped: number;
  note: string | null;
  set_article_id: string | null;
  set_name: string | null;
  set_qty: number | null;
}

export interface PickTask {
  id: string;
  order_id: string;
  order_number: string;
  line_id: string;
  sequence: number;
  status: 'offen' | 'erledigt' | 'abgebrochen';
  article_id: string;
  sku: string;
  article_name: string;
  unit: string;
  owner_id: string;
  owner_name: string;
  from_location_id: string;
  location_code: string;
  warehouse_code: string;
  lot_id: string | null;
  lot_code: string | null;
  best_before: string | null;
  qty_planned: number;
  qty_picked: number;
  hazmat: boolean;
  requires_lot: boolean;
  priority: number;
  container: string | null;
  wave_id: string | null;
}

export interface PicklistResult {
  tasks: number;
  shortfalls: { sku: string; article_name: string; missing: number }[];
}

export type PurchaseStatus = 'entwurf' | 'bestellt' | 'teilgeliefert' | 'geliefert' | 'storniert';

export interface PurchaseOrder {
  id: string;
  number: string;
  reference: string | null;
  owner_id: string;
  owner_name: string;
  supplier_id: string;
  supplier_name: string;
  warehouse_id: string;
  warehouse_code: string;
  status: PurchaseStatus;
  ordered_at: string | null;
  expected_at: string | null;
  note: string | null;
  created_at: string;
  line_count: number;
  qty_ordered: number;
  qty_delivered: number;
  value_cents: number;
}

export interface PurchaseLine {
  id: string;
  position: number;
  article_id: string;
  sku: string;
  article_name: string;
  unit: string;
  qty_ordered: number;
  qty_delivered: number;
  price_cents: number | null;
  note: string | null;
}

export interface PurchaseSuggestion {
  article_id: string;
  sku: string;
  article_name: string;
  unit: string;
  owner_id: string;
  owner_name: string;
  stock: number;
  min_stock: number;
  shortfall: number;
  on_order: number;
  price_cents: number | null;
  supplier_id: string | null;
  supplier_name: string | null;
  supplier_sku: string | null;
  lead_days: number | null;
  min_order_qty: number | null;
}

export type ReturnStatus = 'angekuendigt' | 'teilweise' | 'abgeschlossen' | 'storniert';
export type LineCondition = 'verkaufsfaehig' | 'sperrbestand' | 'vernichtung';

export interface ReturnOrder {
  id: string;
  number: string;
  owner_id: string;
  owner_name: string;
  customer_id: string | null;
  customer_name: string | null;
  outbound_order_id: string | null;
  outbound_number: string | null;
  warehouse_id: string;
  warehouse_code: string;
  reason: string | null;
  status: ReturnStatus;
  note: string | null;
  created_at: string;
  closed_at: string | null;
  line_count: number;
  qty_expected: number;
  qty_received: number;
}

export interface ReturnLine {
  id: string;
  position: number;
  article_id: string;
  sku: string;
  article_name: string;
  unit: string;
  requires_lot: boolean;
  qty_expected: number;
  qty_received: number;
  condition: LineCondition;
  note: string | null;
}

export type StorageModel = 'einheiten' | 'stellplaetze' | 'beides';

export interface ServiceItem {
  id: string;
  owner_id: string | null;
  owner_name: string | null;
  code: string;
  bezeichnung: string;
  einheit: string;
  preis_cents: number;
  aktiv: boolean;
}

export interface RateCard {
  id: string;
  owner_id: string;
  owner_name: string;
  storage_model: StorageModel;
  storage_millicents_per_unit_day: number;
  slot_millicents_per_day: number;
  contracted_slots: number;
  contracted_slot_cents_per_month: number;
  handling_in_cents: number;
  handling_out_cents: number;
  min_monthly_cents: number;
  valid_from: string;
  note: string | null;
}

export interface BillingEvent {
  id: string;
  owner_id: string;
  owner_name: string;
  event_date: string;
  type: string;
  description: string;
  qty: number;
  amount_cents: number;
  invoice_id: string | null;
  invoice_number: string | null;
}

/**
 * Steuerkategorie nach UNTDID 5305 - dieselben Schlüssel wie in der
 * E-Rechnung: S Regelsatz, AE Reverse Charge, G Ausfuhr, E Kleinunternehmer.
 */
export type TaxCategory = 'S' | 'AE' | 'G' | 'E';

export const TAX_CATEGORY_LABEL: Record<TaxCategory, string> = {
  S: 'Regelsatz',
  AE: 'Reverse Charge',
  G: 'Ausfuhr',
  E: 'ohne Steuer',
};

export type TaxTreatment = 'inland' | 'eu_reverse_charge' | 'drittland';

export const TAX_TREATMENT_LABEL: Record<TaxTreatment, string> = {
  inland: 'Inland (Regelsatz)',
  eu_reverse_charge: 'EU, Reverse Charge',
  drittland: 'Drittland (Ausfuhr)',
};

export interface TaxSubtotal {
  category: TaxCategory;
  ratePercent: number;
  netCents: number;
  taxCents: number;
  exemptionReason: string | null;
}

export interface Invoice {
  id: string;
  number: string;
  owner_id: string;
  owner_name: string;
  period_start: string;
  period_end: string;
  status: 'entwurf' | 'festgeschrieben' | 'storniert';
  net_cents: number;
  tax_rate_percent: number;
  tax_cents: number;
  gross_cents: number;
  due_date: string | null;
  datev_export_id: string | null;
  paid_cents: number;
  tax_note: string | null;
  /** Gesetzt, wenn dieser Beleg selbst der Storno einer Rechnung ist. */
  cancels_invoice_id: string | null;
  cancels_number: string | null;
  /** Gesetzt, wenn diese Rechnung durch einen Storno aufgehoben wurde. */
  cancelled_by_invoice_id: string | null;
  cancelled_by_number: string | null;
  cancel_reason: string | null;
  note: string | null;
  created_at: string;
  booked_at: string | null;
  line_count: number;
}

export interface InvoiceLine {
  id: string;
  position: number;
  description: string;
  qty: number;
  unit: string;
  unit_price_millicents: number;
  amount_cents: number;
  tax_rate_percent: number;
  tax_category: TaxCategory;
  tax_cents: number;
}

export interface InvoiceDraft {
  ownerId: string;
  ownerName: string;
  periodStart: string;
  periodEnd: string;
  lines: {
    description: string;
    qty: number;
    unit: string;
    unitPriceMillicents: number;
    amountCents: number;
    taxCategory: TaxCategory;
    taxRatePercent: number;
    exemptionReason: string | null;
  }[];
  netCents: number;
  taxCents: number;
  grossCents: number;
  taxSubtotals: TaxSubtotal[];
  taxNote: string | null;
  eventIds: string[];
  snapshotIds: string[];
  fondAufstellung: FondAufstellungZeile[];
}

export interface FondAufstellungZeile {
  fond_id: string | null;
  fond: string;
  anzahl: number;
  menge_gesamt: number;
  auftraege: { number: string; shipped_at: string | null; qty: number }[];
}

export interface AuditChange {
  field: string;
  from: unknown;
  to: unknown;
}

export interface AuditEntry {
  id: string;
  user_name: string | null;
  entity: string;
  entity_id: string | null;
  label: string | null;
  action: 'angelegt' | 'geaendert' | 'geloescht';
  changes: AuditChange[];
  created_at: string;
}

export interface ArchivedDocument {
  id: string;
  doc_type: string;
  reference_id: string | null;
  number: string | null;
  filename: string;
  mime_type: string;
  sha256: string;
  size_bytes: number;
  created_at: string;
  created_by_name: string | null;
}

export interface LotSummary {
  id: string;
  code: string;
  sku: string;
  article_name: string;
  best_before: string | null;
  qty: number;
}

export interface LotTrace {
  lot: {
    id: string;
    code: string;
    best_before: string | null;
    sku: string;
    article_name: string;
    owner_name: string;
    supplier_ref: string | null;
  };
  stock: { location_code: string; warehouse_code: string; qty: number }[];
  movements: {
    id: string;
    booked_at: string;
    reason: string;
    qty: number;
    from_code: string | null;
    to_code: string | null;
    document_ref: string | null;
    user_name: string | null;
  }[];
  deliveries: {
    order_number: string;
    customer_name: string | null;
    shipped_at: string | null;
    qty: number;
  }[];
}

/** Bewertung einer Sendung nach ADR — Rechenhilfe, keine Freigabe. */
export interface GefahrgutZeile {
  sku: string;
  article_name: string;
  angabe: string | null;
  kategorie: number | null;
  begrenzte_menge: boolean;
  menge: number | null;
  punkte: number | null;
  fehlt: string[];
}

export interface Gefahrgut {
  zeilen: GefahrgutZeile[];
  punkte: number;
  grenze: number;
  freigestellt: boolean;
  meldung: string;
  warnungen: string[];
  betroffen: number;
}
